import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import {
  RadioGroupOption,
  SelectInputOption,
} from '@/components/form/baseInputs/inputTypes';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import { ContextualHelpTrigger } from '@/modules/content/components/ContextualHelpTrigger';
import { useInsurancePolicyDetailsPremiumPaymentSourceOptions } from '@/modules/entities/InsurancePolicyDetailsSubform/hooks/useInsurancePolicyDetailsPremiumPaymentSourceOptions';
import {
  HypotheticalInsuranceEndsOnKind,
  HypotheticalInsuranceTransferFrequency,
} from '@/types/schema';

import { useInsurancePolicyPremiumTransferTaxTypeOptions } from '../hooks/useInsurancePolicyPremiumTransferTaxTypeOptions';
import { useTransferHelpText } from '../hooks/useTransferHelpText';
import {
  HypotheticalILITFormModalShape,
  HypotheticalILITFormModalShapePaths,
} from '../HypotheticalILITModal.types';

const TRANSFER_FREQUENCY_OPTIONS: RadioGroupOption<
  HypotheticalILITFormModalShape['transferFrequency']
>[] = [
  {
    label: 'One time',
    value: HypotheticalInsuranceTransferFrequency.OneTime,
  },
  {
    label: 'Annual',
    value: HypotheticalInsuranceTransferFrequency.Annual,
  },
];

const TRANSFER_ENDS_ON_OPTIONS: SelectInputOption<
  HypotheticalILITFormModalShape['endsOnKind']
>[] = [
  {
    display: "Policy holder's death",
    value: HypotheticalInsuranceEndsOnKind.PolicyHolderDeath,
  },
  {
    display: 'Trust termination',
    value: HypotheticalInsuranceEndsOnKind.TrustTermination,
  },
  {
    display: 'Year',
    value: HypotheticalInsuranceEndsOnKind.Year,
  },
];

export function HypotheticalILITTransferDetailsBody() {
  const { control } = useFormContext<HypotheticalILITFormModalShape>();
  const transferTaxKindOptions =
    useInsurancePolicyPremiumTransferTaxTypeOptions();
  const {
    options: transferSourceOptions,
    data,
    loading,
  } = useInsurancePolicyDetailsPremiumPaymentSourceOptions({
    isHypothetical: true,
    runQuery: true,
  });
  const { helpText, helpTextVariant } = useTransferHelpText(data);

  return (
    <Card variant="filled" sx={{ p: 2, pb: 4 }}>
      <Stack spacing={0.5} sx={{ pb: 3 }}>
        <Typography variant="title-l">Funding details</Typography>
        <Typography variant="body-xs">
          Specify details of the funding that will be used to cover policy
          premium payments. This does not include the transfer of the policy
          cash value if transferring an existing policy.
        </Typography>
      </Stack>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'transferName' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Transfer name"
            required
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow sx={{ alignItems: 'end' }}>
        <FormLayoutItem>
          <FormAwareSelectInput<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'transferFromId' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Transfer from"
            options={transferSourceOptions}
            required
            loading={loading}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow
        sx={{
          // style is necessary to align the radio group with the text input
          alignItems: 'end',
        }}
      >
        <FormLayoutItem width={7}>
          <FormAwareCurrencyInput<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'transferValue' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Amount to transfer"
            required
            isDecimalJSInput
            decimalScale={0}
            helpText={helpText}
            helpTextVariant={helpTextVariant}
            contextualHelp={
              <PopperContent body="The amount that will be used to cover policy premium payments. This does not include the transfer of the policy cash value if transferring an existing policy." />
            }
          />
        </FormLayoutItem>
        <FormLayoutItem
          width={5}
          sx={{
            // style is necessary to align with the text input next to it
            height: '100%',
            pt: 3,
          }}
        >
          <FormAwareRadioGroup<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'transferFrequency' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Transfer frequency"
            row
            required
            hideLabel
            options={TRANSFER_FREQUENCY_OPTIONS}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <OneTimeTransferDetails />
      <AnnualTransferDetails />
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareSelectInput<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'transferTaxKind' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="Type of transfer (gift tax purposes)"
            options={transferTaxKindOptions}
            required
            contextualHelp={
              <PopperContent
                body="Learn more about transfer types and associated estate tax treatments"
                actions={
                  <ContextualHelpTrigger
                    contextualHelpId="transferTypes"
                    display="Learn more"
                  />
                }
                actionsSx={{ marginBottom: 0, marginRight: 0 }}
              />
            }
          />
        </FormLayoutItem>
      </FormLayoutRow>
    </Card>
  );
}

function OneTimeTransferDetails() {
  const { control } = useFormContext<HypotheticalILITFormModalShape>();
  const [transferFrequency] = useWatch({
    control,
    name: [
      'transferFrequency' as const satisfies HypotheticalILITFormModalShapePaths,
    ],
  });

  if (transferFrequency !== HypotheticalInsuranceTransferFrequency.OneTime) {
    return null;
  }

  return (
    <FormLayoutRow>
      <FormLayoutItem>
        <FormAwareFormattedNumberInput<HypotheticalILITFormModalShape>
          control={control}
          fieldName={
            'startYear' as const satisfies HypotheticalILITFormModalShapePaths
          }
          label="Year of transfer"
          decimalScale={0}
          required
        />
      </FormLayoutItem>
    </FormLayoutRow>
  );
}

function AnnualTransferDetails() {
  const { control } = useFormContext<HypotheticalILITFormModalShape>();
  const [transferFrequency, endsOnKind] = useWatch({
    control,
    name: [
      'transferFrequency' as const satisfies HypotheticalILITFormModalShapePaths,
      'endsOnKind' as const satisfies HypotheticalILITFormModalShapePaths,
    ],
  });

  const layoutItemWidth = useMemo(() => {
    if (
      endsOnKind === HypotheticalInsuranceEndsOnKind.PolicyHolderDeath ||
      endsOnKind === HypotheticalInsuranceEndsOnKind.TrustTermination
    ) {
      return 6;
    }
    return 4;
  }, [endsOnKind]);

  if (transferFrequency !== HypotheticalInsuranceTransferFrequency.Annual) {
    return null;
  }

  return (
    <FormLayoutRow>
      <FormLayoutItem width={layoutItemWidth}>
        <FormAwareFormattedNumberInput<HypotheticalILITFormModalShape>
          control={control}
          fieldName={
            'startYear' as const satisfies HypotheticalILITFormModalShapePaths
          }
          label="Start year"
          decimalScale={0}
          required
        />
      </FormLayoutItem>
      <FormLayoutItem width={layoutItemWidth}>
        <FormAwareSelectInput<HypotheticalILITFormModalShape>
          control={control}
          fieldName={
            'endsOnKind' as const satisfies HypotheticalILITFormModalShapePaths
          }
          label="Ends on"
          required
          options={TRANSFER_ENDS_ON_OPTIONS}
        />
      </FormLayoutItem>
      {endsOnKind === HypotheticalInsuranceEndsOnKind.Year && (
        <FormLayoutItem width={layoutItemWidth}>
          <FormAwareFormattedNumberInput<HypotheticalILITFormModalShape>
            control={control}
            fieldName={
              'endYear' as const satisfies HypotheticalILITFormModalShapePaths
            }
            label="End year"
            decimalScale={0}
            required
          />
        </FormLayoutItem>
      )}
    </FormLayoutRow>
  );
}
